import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, EmptyState, Icon, Modal, Section, useToast } from '@efira/ui';
import type { IconName } from '@efira/ui';

import { useDirectorMessage } from '../../api/queries';
import { coinsAccusative, likes as likesWord } from '../../lib/plural';
import { useConsoleStream } from '../stream';
import type { ChatLineItem } from '../stream';
import { LIVE_WORDS, useLiveKind } from '../liveState';

/**
 * Лента эфира: ВСЕ события зрителя одной лентой.
 *
 * НЕ ТОЛЬКО КОММЕНТАРИИ - так у эталона, и это верно: вход, репост и подписка
 * тоже повод сказать спасибо, а отдельная лента под каждый тип заставила бы
 * стримера смотреть в четыре места сразу.
 *
 * ДВЕ РАЗНЫЕ ЗАГЛУШКИ. «Нет сессии» и «сессия идёт, зрители молчат» требуют
 * разных действий: в первом случае - выбрать игру, во втором - подождать или
 * позвать зрителей. Одна заглушка на оба случая - это «нет данных», то есть
 * ничего.
 */
const TYPES: {
  id: ChatLineItem['type'];
  icon: IconName;
  label: string;
  /** Как читается строка этого типа. */
  say: (line: ChatLineItem, name: string) => string;
}[] = [
  { id: 'comment', icon: 'chat', label: 'Комментарии', say: (line) => line.text ?? '' },
  {
    id: 'like',
    icon: 'heart',
    label: 'Лайки',
    /* Склонение, а не двоеточие: «поставил лайков: 1» - находка владельца.
       Формат один на продукт (`lib/plural.ts`). */
    say: (line) => `поставил ${likesWord(line.count ?? 1)}`,
  },
  {
    id: 'follow',
    icon: 'personAdd',
    label: 'Подписки',
    say: (line) => ((line.count ?? 1) > 1 ? `подписался · ×${line.count}` : 'подписался на канал'),
  },
  {
    id: 'share',
    icon: 'share',
    label: 'Репосты',
    say: (line) => ((line.count ?? 1) > 1 ? `поделился эфиром ×${line.count}` : 'поделился эфиром'),
  },
  {
    id: 'join',
    icon: 'eye',
    label: 'Входы',
    say: (line) => ((line.count ?? 1) > 1 ? `заходил ×${line.count}` : 'зашёл в эфир'),
  },
  {
    id: 'gift',
    icon: 'gift',
    label: 'Подарки',
    /* «на ... монетУ»: после предлога «на» - винительный падеж. */
    say: (line) => `отправил подарок на ${coinsAccusative(line.coins ?? 0)}`,
  },
];

/** Пять флажков фильтра, как у эталона. Подарки в фильтр не входят. */
const FILTERABLE: ChatLineItem['type'][] = ['comment', 'follow', 'like', 'share', 'join'];

export function Chat() {
  const stream = useConsoleStream();
  const [filter, setFilter] = useState<ChatLineItem['type'][]>([...FILTERABLE, 'gift']);
  const [filterOpen, setFilterOpen] = useState(false);
  const [card, setCard] = useState<string | null>(null);

  const shown = useMemo(
    () => stream.chat.filter((line) => filter.includes(line.type)),
    [stream.chat, filter],
  );

  return (
    /* ШАПКА СЕКЦИИ - ОБЩИЙ КОМПОНЕНТ, а не своя копия. Своя копия и была
       причиной трёх разных видов шапки на продукт: залитая полоса во всю
       ширину справа и заголовок слева в левой колонке (находка владельца
       2026-09-25). Доля высоты (1,21 к 1 у эталона) остаётся здесь - это
       раскладка колонки, а не вид секции. */
    <div data-right="chat-section" className="flex min-h-0 flex-[1.21] flex-col">
      <Section
        title="Чат"
        id="chat"
        fill
        scroll={false}
        toggleData={{ 'data-right': 'chat-toggle' }}
        actions={
          <Button
            variant="ghost"
            size="s"
            className="px-1"
            data-right="chat-filter-btn"
            aria-label="Какие события показывать"
            title="Какие события показывать"
            onClick={() => {
              setFilterOpen(true);
            }}
          >
            <Icon name="settings" size="sm" />
            {/* Число выбранного - рядом со значком: фильтр, о котором
                забыли, выглядит как пропавшие события. */}
            {filter.length < FILTERABLE.length + 1 && (
              <Badge variant="secondary">{filter.length}</Badge>
            )}
          </Button>
        }
      >
        <Feed
          lines={shown}
          onPickActor={(id) => {
            setCard(id);
          }}
        />
      </Section>

      {filterOpen && (
        <FilterModal
          value={filter}
          onClose={() => {
            setFilterOpen(false);
          }}
          onApply={(next) => {
            setFilter(next);
            setFilterOpen(false);
          }}
        />
      )}

      {card !== null && (
        <UserCard
          actorId={card}
          onClose={() => {
            setCard(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Лента с автопрокруткой.
 *
 * АВТОПРОКРУТКА ВЫКЛЮЧАЕТСЯ САМА, стоит стримеру прокрутить вверх: лента,
 * которая утаскивает к низу, пока человек читает старое сообщение, - это
 * лента, в которой ничего не прочитать. Возврат к свежим - кнопкой.
 */
function Feed({
  lines,
  onPickActor,
}: {
  lines: ChatLineItem[];
  onPickActor: (id: string) => void;
}) {
  /* Имя ленты для чтеца - из одного источника: во время симулятора это
     лента симулятора, а не эфира (единица 22). */
  const liveKind = useLiveKind();
  const stream = useConsoleStream();
  const ref = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(true);

  /**
   * АВТОПРОКРУТКА РЕШАЕТСЯ ЗАМЕРОМ, А НЕ ПАМЯТЬЮ.
   *
   * Замерено на живом потоке: при десятке событий в секунду прокрутить ленту
   * вверх было НЕВОЗМОЖНО. Обе прежние редакции - состояние и реф -
   * проигрывали гонку: строка приходила раньше, чем браузер успевал
   * сообщить о прокрутке, эффект видел «прижата» и утаскивал ленту обратно.
   *
   * Здесь решение принимается по фактическому расстоянию до низа В МОМЕНТ
   * добавления строки. Расстояние больше высоты пары строк - человек читает
   * старое, и трогать его нельзя.
   */
  useEffect(() => {
    const node = ref.current;

    if (node === null) {
      return;
    }

    const fromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;

    if (fromBottom < 80) {
      node.scrollTop = node.scrollHeight;
    }
  }, [lines]);

  if (lines.length === 0) {
    /* Область прокрутки существует и при пустой ленте: она объявлена чтецу
       и на неё ссылается проверка независимых областей прокрутки консоли.
       Появляющаяся область - это область, которой для чтеца не было. */
    return (
      <div
        data-right="chat-feed"
        data-console-scroll="chat"
        tabIndex={0}
        role="log"
        aria-label={LIVE_WORDS[liveKind].feed}
        data-live-place="chat-feed"
        data-live-state={liveKind}
        data-right-empty=""
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
      >
        {/* Ветки «эфира нет» здесь больше нет: без сессии колонка целиком
            показывает ОДНО состояние (`RightColumn`, находка аудита 11). */}
        <EmptyState id="chat-empty" icon="chat" title="Зрители пока молчат">
          Первое сообщение появится здесь само — позовите их в чат или запустите симулятор, чтобы
          посмотреть, как это выглядит.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={ref}
        data-right="chat-feed"
        data-console-scroll="chat"
        tabIndex={0}
        role="log"
        aria-label={LIVE_WORDS[liveKind].feed}
        data-live-place="chat-feed"
        data-live-state={liveKind}
        /*
         * ВЕРХНЯЯ СТРОКА НЕ РЕЖЕТСЯ ПОПОЛАМ - `scroll-fade-t` ПРЕСЕТА.
         *
         * Снимок владельца (единица 17): лента прокручена, и первая
         * видимая строка обрезана по горизонтали ровно посередине - имя
         * читается наполовину. Обрезанная строка выглядит как поломка
         * вёрстки, а не как продолжение списка.
         *
         * Утилита пресета гасит верхнюю кромку, пока над ней есть
         * содержимое, и убирает затухание, когда лента прокручена в
         * начало. Своего кода нет.
         */
        className="scroll-fade-t h-full overflow-y-auto px-3 pb-3"
        onScroll={(event) => {
          const node = event.currentTarget;
          setStuck(node.scrollHeight - node.scrollTop - node.clientHeight < 80);
        }}
      >
        {lines.map((line) => {
          const kind = TYPES.find((item) => item.id === line.type);
          const member = stream.members[line.actorId];
          const name = member?.name ?? 'Зритель';

          return (
            <div
              key={line.id}
              data-right-chat-row={line.type}
              className="flex items-start gap-2 py-1 text-sm/5"
            >
              {/* ЛЕВАЯ КОЛОНКА ОДНОЙ ШИРИНЫ у всех строк: с аватаром и без
                  него текст обязан начинаться по одной вертикали, иначе лента
                  на быстром потоке читается как рваная. */}
              <span className="mt-1 grid size-5 shrink-0 place-items-center">
                {member?.avatarUrl === undefined ? (
                  <Icon name={kind?.icon ?? 'chat'} size="sm" tone="muted" />
                ) : (
                  <img src={member.avatarUrl} alt="" className="size-5 rounded-full" />
                )}
              </span>
              <p className="min-w-0 flex-1">
                <Button
                  variant="ghost"

                  data-right-chat-actor={line.actorId}
                  onClick={() => {
                    onPickActor(line.actorId);
                  }}
                >
                  {name}
                </Button>{' '}
                <span className="text-foreground/80">{kind?.say(line, name) ?? ''}</span>
              </p>
            </div>
          );
        })}
      </div>

      {!stuck && (
        <Button
          variant="primary"
          size="s"
          className="absolute inset-x-0 bottom-2 mx-auto w-fit"
          data-right="chat-jump"
          onClick={() => {
            setStuck(true);

            if (ref.current !== null) {
              ref.current.scrollTop = ref.current.scrollHeight;
            }
          }}
        >
          К свежим сообщениям
        </Button>
      )}
    </div>
  );
}

/** Фильтр событий: пять флажков, «Отмена» и «Применить». */
function FilterModal({
  value,
  onClose,
  onApply,
}: {
  value: ChatLineItem['type'][];
  onClose: () => void;
  onApply: (next: ChatLineItem['type'][]) => void;
}) {
  /* Окно монтируется только открытым, поэтому черновик берётся из свойств
     один раз - эффект синхронизации был бы лишним рендером на каждое
     открытие и правилом, которое легко забыть. */
  const [draft, setDraft] = useState(value);

  const toggle = (id: ChatLineItem['type'], on: boolean) => {
    setDraft((current) =>
      on ? [...new Set([...current, id])] : current.filter((item) => item !== id),
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Какие события показывать"
      /* Снятый флажок - несохранённая правка: промах мимо окна не должен
         молча вернуть всё как было. */
      dirty={draft.length !== value.length}
      actions={
        <>
          <Button variant="secondary" data-right="chat-filter-cancel" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            data-right="chat-filter-apply"
            onClick={() => {
              onApply(draft.includes('gift') ? draft : [...draft, 'gift']);
            }}
          >
            Применить
          </Button>
        </>
      }
    >
      <div data-right="chat-filter-modal" className="flex flex-col gap-2">
        {FILTERABLE.map((id) => {
          const kind = TYPES.find((item) => item.id === id);

          return (
            <label key={id} className="flex items-center gap-2 text-sm">
              <Checkbox
                data-right-chat-filter={id}
                checked={draft.includes(id)}
                onCheckedChange={(next) => {
                  toggle(id, next === true);
                }}
              />
              {kind?.label ?? id}
            </label>
          );
        })}

        <p className="text-foreground/80 text-sm">
          Подарки видно всегда: за них платят, и прятать их незачем.
        </p>
      </div>
    </Modal>
  );
}

/**
 * Карточка зрителя.
 *
 * ССЫЛКА НА ПРОФИЛЬ - ВНЕШНЯЯ, и об этом сказано значком и `rel=noopener`:
 * нажатие уводит из консоли посреди эфира, и человек вправе знать об этом до
 * нажатия, а не после.
 */
function UserCard({ actorId, onClose }: { actorId: string; onClose: () => void }) {
  const stream = useConsoleStream();
  const message = useDirectorMessage();
  const toast = useToast();
  const member = stream.members[actorId];
  const name = member?.name ?? 'Зритель';
  const handle = actorId.replace(/^@/, '');

  return (
    <Modal
      open
      onClose={onClose}
      title="Зритель"
      actions={
        <Button variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      }
    >
      <div data-right="user-card" className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          {member?.avatarUrl === undefined ? (
            <span className="bg-border grid size-12 shrink-0 place-items-center rounded-full">
              <Icon name="person" size="lg" />
            </span>
          ) : (
            <img src={member.avatarUrl} alt="" className="size-12 shrink-0 rounded-full" />
          )}

          <div className="min-w-0">
            <p className="truncate font-semibold">{name}</p>
            <p className="text-foreground/80 truncate text-sm">@{handle}</p>
          </div>
        </div>

        <a
          data-right="user-card-link"
          href={`https://www.tiktok.com/@${handle}`}
          target="_blank"
          rel="noreferrer noopener"
          className="text-foreground inline-flex items-center gap-2 self-start text-sm font-medium underline underline-offset-2"
        >
          <Icon name="open" size="sm" />
          Профиль в TikTok — откроется в новой вкладке
        </a>

        <Button
          variant="primary"
          size="s"
          className="self-start"
          data-right="user-card-shoutout"
          onClick={() => {
            message.mutate(
              { text: `Спасибо, ${name}!`, seconds: 10 },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Спасибо ушло на оверлей' });
                  onClose();
                },
                onError: () => {
                  toast.show({
                    tone: 'warning',
                    title: 'Не отправилось',
                    text: 'Сообщение показывается на оверлее, а оверлей сейчас не идёт.',
                  });
                },
              },
            );
          }}
        >
          Сказать спасибо
        </Button>

        <p className="text-foreground/80 text-sm">
          Текст «Спасибо, {name}!» появится на оверлее на десять секунд. Свой текст — кнопкой
          «Сообщение зрителям» в левой колонке.
        </p>
      </div>
    </Modal>
  );
}
